import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useRsvp } from '@/src/modules/rsvp';
import {
  Calendar,
  MapPin,
  Clock,
  Music,
  Heart,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  Volume2,
  VolumeX,
  Share2,
  Navigation,
  Info,
  CalendarCheck,
  Send,
  Users,
} from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
  safeExtractCeremonies,
} from '@/src/infrastructure/lib/weddingDataHelper';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-sikh-anand-karaj',
  name: 'Sikh Anand Karaj — Sacred Journey',
  occasionType: 'WEDDING',
  description:
    'Majestic Sikh Punjabi wedding microsite featuring an 8-chapter guest journey: Waheguru soft gate with Ik Onkar crest, mustard field hero, Phulkari family gratitude letter, twilight Gurdwara save-the-date, multi-event schedule with dress codes, Gurdwara etiquette, Khanda sacred ceremony beat, wedding film teaser, and RSVP with blessings wall.',
  isPremium: true,
  tags: [
    'wedding',
    'sikh',
    'punjabi',
    'anand-karaj',
    'phulkari',
    'gurdwara',
    'parallax',
    'sacred',
    'dress-code',
    'premium',
    'khanda',
    'interactive',
  ],
  colorScheme: { primary: '#7a3520', accent: '#d4af37', bg: '#180a0c' },
};

// --- Web Audio Synthesizer for Ambient Raga Chimes ---
class SacredAudioPlayer {
  private ctx: AudioContext | null = null;
  private isPlaying = false;
  private timer: any = null;

  start() {
    if (this.isPlaying) return;
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
      if (this.ctx.state === 'suspended') {
        this.ctx.resume();
      }
      this.isPlaying = true;
      this.playCycle();
    } catch {
      // AudioContext unavailable or restricted
    }
  }

  stop() {
    this.isPlaying = false;
    if (this.timer) clearTimeout(this.timer);
    if (this.ctx) {
      try {
        this.ctx.close();
      } catch {}
      this.ctx = null;
    }
  }

  private playCycle = () => {
    if (!this.isPlaying || !this.ctx) return;
    // Pentatonic meditative frequencies (Sa, Re, Ga, Pa, Dha - Bilawal/Kalyan blend)
    const notes = [293.66, 329.63, 369.99, 440.0, 493.88, 587.33];
    const freq = notes[Math.floor(Math.random() * notes.length)];
    this.playTone(freq, 2.8);
    const delay = 1400 + Math.random() * 1600;
    this.timer = setTimeout(this.playCycle, delay);
  };

  private playTone(freq: number, duration: number) {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);

    // Warm envelope
    gain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.035, this.ctx.currentTime + 0.35);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }
}

// --- Icons & Crests ---
function IkOnkarCrest({ className = 'w-12 h-12' }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" className={className} aria-label="Ik Onkar Emblem">
      {/* Gurmukhi Ik Onkar Calligraphic Symbol */}
      <path d="M 28 36 C 22 36 18 42 18 48 C 18 55 24 61 31 61 C 38 61 44 56 47 49 C 50 42 56 36 64 36 C 73 36 80 43 80 52 C 80 62 71 70 60 70 C 56 70 51 68 47 65 L 44 71 C 49 75 55 77 61 77 C 76 77 87 66 87 51 C 87 38 77 29 64 29 C 54 29 46 35 42 43 C 39 49 35 53 30 53 C 27 53 25 51 25 48 C 25 45 27 42 30 42 C 32 42 34 43 35 45 L 40 40 C 37 37 33 36 28 36 Z" />
      <path d="M 64 29 C 64 21 72 15 81 15 C 83 15 85 15 87 16 L 85 22 C 84 22 83 22 81 22 C 76 22 71 25 71 29 Z" />
      <circle cx="82" cy="18" r="3" />
    </svg>
  );
}

function KhandaEmblem({ className = 'w-16 h-16' }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" fill="currentColor" className={className} aria-label="Khanda Emblem">
      {/* Central Double-edged Sword */}
      <path d="M 48 8 L 52 8 L 52 70 L 55 70 L 55 76 L 52 76 L 52 88 L 48 88 L 48 76 L 45 76 L 45 70 L 48 70 Z" />
      {/* Central Chakar (quoit circle) */}
      <circle cx="50" cy="46" r="16" fill="none" stroke="currentColor" strokeWidth="4" />
      {/* Left Curved Kirpan */}
      <path d="M 22 28 C 34 38 34 60 20 74 C 18 72 20 62 26 54 C 30 46 28 36 22 28 Z" />
      {/* Right Curved Kirpan */}
      <path d="M 78 28 C 66 38 66 60 80 74 C 82 72 80 62 74 54 C 70 46 72 36 78 28 Z" />
    </svg>
  );
}

function GurdwaraDomeIllustration() {
  return (
    <svg viewBox="0 0 160 100" fill="none" className="w-40 h-24 mx-auto text-[#d4af37]" stroke="currentColor">
      {/* Main Dome */}
      <path d="M 80 15 C 65 30 50 45 50 65 L 110 65 C 110 45 95 30 80 15 Z" fill="rgba(212, 175, 55, 0.15)" strokeWidth="2" />
      {/* Kalash / Spire */}
      <path d="M 80 5 L 80 15 M 76 10 L 84 10" strokeWidth="2" strokeLinecap="round" />
      {/* Base Cornice */}
      <rect x="42" y="65" width="76" height="6" rx="2" fill="currentColor" />
      {/* Pillars */}
      <line x1="52" y1="71" x2="52" y2="92" strokeWidth="2.5" />
      <line x1="68" y1="71" x2="68" y2="92" strokeWidth="2" />
      <line x1="92" y1="71" x2="92" y2="92" strokeWidth="2" />
      <line x1="108" y1="71" x2="108" y2="92" strokeWidth="2.5" />
      {/* Ground plinth */}
      <line x1="30" y1="92" x2="130" y2="92" strokeWidth="3" strokeLinecap="round" />
      {/* Flags on sides */}
      <path d="M 40 45 L 40 65 M 40 45 L 48 50 L 40 55" strokeWidth="1.5" />
      <path d="M 120 45 L 120 65 M 120 45 L 128 50 L 120 55" strokeWidth="1.5" />
    </svg>
  );
}

export function SikhAnandKarajTemplate({ data }: { data: InvitationData }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<SacredAudioPlayer | null>(null);

  const [isOpened, setIsOpened] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [now, setNow] = useState(Date.now());

  // RSVP Form State
  const isLive = data.status === 'PUBLISHED';
  const rsvp = useRsvp(data.invitationId || data.id, isLive);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [attending, setAttending] = useState<'YES' | 'NO'>('YES');
  const [guestCount, setGuestCount] = useState('2');
  const [blessingMessage, setBlessingMessage] = useState('');
  const [wishes, setWishes] = useState<Array<{ name: string; msg: string; date: string }>>([
    {
      name: 'Harpreet & Jasleen Brar',
      msg: 'May Waheguru bless your sacred Anand Karaj with boundless joy and everlasting peace!',
      date: 'Just now',
    },
    {
      name: 'Gurinder Singh Sidhu',
      msg: 'Lakh Lakh Vadhaiyan to both families. So excited for the Jaago and ceremony!',
      date: '2 hours ago',
    },
  ]);

  // Check if mounted in thumbnail card (bypass unboxing gate)
  useEffect(() => {
    if (rootRef.current?.closest('[data-template-thumbnail]')) {
      setIsOpened(true);
    }
  }, []);

  // Timer tick for countdown
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Cleanup audio player on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.stop();
    };
  }, []);

  const toggleAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new SacredAudioPlayer();
    }
    if (isMuted) {
      audioRef.current.start();
      setIsMuted(false);
    } else {
      audioRef.current.stop();
      setIsMuted(true);
    }
  }, [isMuted]);

  const handleOpenGate = useCallback(() => {
    setIsOpened(true);
    if (!audioRef.current) {
      audioRef.current = new SacredAudioPlayer();
    }
    audioRef.current.start();
    setIsMuted(false);
  }, []);

  // Resolve Couple Names and Event Data
  const couple = useMemo(() => {
    const extracted = extractCoupleNames(data.eventName);
    if (extracted && extracted.groom && extracted.bride) {
      return { groom: extracted.groom, bride: extracted.bride };
    }
    if (data.hostName) {
      return { groom: data.hostName, bride: 'Bride' };
    }
    return { groom: 'The Groom', bride: 'The Bride' };
  }, [data.eventName, data.hostName]);

  const parsedDate = useMemo(() => {
    const parsed = parseFlexibleDate(data.eventDate);
    return parsed || new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
  }, [data.eventDate]);

  // Countdown calculations
  const countdown = useMemo(() => {
    const diff = Math.max(0, parsedDate.getTime() - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { days, hours, minutes, seconds };
  }, [parsedDate, now]);

  // Ceremonies
  const ceremonies = useMemo(() => {
    const raw = (data as any).weddingEvents || data.customData?.weddingEvents;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((c: any, index: number) => ({
        id: c.key || `event-${index}`,
        title: c.name || c.title || 'Ceremony',
        date: c.date ? new Date(c.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Wedding Weekend',
        time: c.time || c.startTime || '10:00 AM',
        venue: c.venue || data.eventVenue || 'Gurdwara Sahib',
        dressCode: c.dressCode || (index === 0 ? 'Festive & Colorful Punjabi' : index === 1 ? 'Pastel Ethnic / Head Covered' : 'Formal Royal Ethnic'),
        note: c.note || c.description || 'Join us with love and blessings.',
      }));
    }
    // Authentic Punjabi Anand Karaj defaults
    const dMinus1 = new Date(parsedDate.getTime() - 24 * 60 * 60 * 1000);
    return [
      {
        id: 'sangeet',
        title: 'Sangeet & Jaago Raat',
        date: dMinus1.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        time: '06:30 PM onwards',
        venue: 'The Grand Pavilion & Courtyard',
        dressCode: 'Vibrant Punjabi Folk & Indo-Western',
        note: 'An evening of spirited boliyan, dhol beats, dancing, and heartfelt celebration.',
      },
      {
        id: 'anand-karaj',
        title: 'Anand Karaj (Sacred Nuptials)',
        date: parsedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        time: '10:00 AM (Baraat) · 11:30 AM (Laavan)',
        venue: data.eventVenue || 'Sri Guru Singh Sabha Gurdwara Sahib',
        dressCode: 'Traditional Modest Pastels (Head covering required)',
        note: 'The auspicious solemnization of marriage in the divine presence of Sri Guru Granth Sahib Ji, followed by Guru Ka Langar.',
      },
      {
        id: 'reception',
        title: 'Doli & Royal Reception',
        date: parsedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        time: '07:30 PM onwards',
        venue: 'The Royal Ballroom & Gardens',
        dressCode: 'Formal Black Tie or Royal Ethnic Finery',
        note: 'Dinner, celebratory toasts, live music, and evening rejoicing.',
      },
    ];
  }, [data, parsedDate]);

  // Couple Hashtag
  const hashtag = useMemo(() => {
    const g = couple.groom.split(' ')[0].replace(/[^a-zA-Z]/g, '');
    const b = couple.bride.split(' ')[0].replace(/[^a-zA-Z]/g, '');
    return `#${g}FoundHis${b}`;
  }, [couple]);

  const handleRsvpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) return;

    if (blessingMessage.trim()) {
      setWishes((prev) => [
        {
          name: guestName.trim(),
          msg: blessingMessage.trim(),
          date: 'Just now',
        },
        ...prev,
      ]);
    }

    rsvp.submit({
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim() || undefined,
      status: attending === 'YES' ? 'ACCEPTED' : 'DECLINED',
      headcount: parseInt(guestCount, 10) || 1,
      message: blessingMessage.trim() || undefined,
    });
  };

  return (
    <div ref={rootRef} className="sak-root sak-phulkari-pattern" data-testid="sikh-anand-karaj-template">
      {/* =========================================================================
          UNBOXING SOFT GATE
          ========================================================================= */}
      {!isOpened && (
        <div className="sak-gate-overlay" data-testid="sak-gate">
          <div className="sak-gate-card">
            <div className="sak-gold-crest">
              <IkOnkarCrest className="w-10 h-10" />
            </div>

            <p className="text-xs uppercase tracking-[0.25em] text-[#d4af37] font-semibold mb-2">
              ੴ ਸਤਿਗੁਰ ਪ੍ਰਸਾਦਿ
            </p>
            <p className="text-[0.8125rem] italic text-[#f3e5ab] mb-4">
              With the eternal blessings of Akal Purakh
            </p>

            <h2 className="sak-serif text-3xl font-bold text-[#fff8eb] leading-snug mb-3">
              {couple.groom} <span className="sak-ampersand">&</span> {couple.bride}
            </h2>

            <p className="text-xs text-[#d6c6b2] mb-6 leading-relaxed">
              Cordially invite you to share in the divine joy of their sacred wedding celebration.
            </p>

            <button
              type="button"
              className="sak-btn-open"
              onClick={handleOpenGate}
              data-testid="sak-btn-open"
            >
              <Sparkles size={16} /> Open The Invitation
            </button>
          </div>
        </div>
      )}

      {/* Floating Audio Equalizer Button */}
      <button
        type="button"
        className="sak-music-btn"
        onClick={toggleAudio}
        aria-label={isMuted ? 'Play ambient music' : 'Mute music'}
        title={isMuted ? 'Play ambient music' : 'Mute music'}
      >
        {isMuted ? (
          <VolumeX size={20} />
        ) : (
          <div className="flex items-center justify-center">
            <span className="sak-eq-bar" />
            <span className="sak-eq-bar" />
            <span className="sak-eq-bar" />
          </div>
        )}
      </button>

      {/* =========================================================================
          CHAPTER 1: HERO SARSON GOLDEN FIELD
          ========================================================================= */}
      <header className="sak-hero-container">
        <div className="sak-sarson-glow" />

        <div className="sak-blessing-tag">
          <IkOnkarCrest className="w-4 h-4 text-[#ffd700]" />
          <span>With the blessings of Waheguru</span>
        </div>

        <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-medium mb-1">
          The Wedding Celebration of
        </p>

        <h1 className="sak-couple-title">
          {couple.groom} <span className="sak-ampersand">&</span> {couple.bride}
        </h1>

        <div className="flex items-center justify-center gap-2 text-[#f3e5ab] text-sm font-medium mt-1">
          <Calendar size={16} className="text-[#d4af37]" />
          <span>
            {parsedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
          <span className="text-[#d4af37]">·</span>
          <MapPin size={16} className="text-[#d4af37]" />
          <span>{data.eventVenue || 'Gurdwara Sahib'}</span>
        </div>

        {/* Live Countdown */}
        <div className="sak-countdown-grid" aria-label="Countdown to Anand Karaj">
          <div className="sak-countdown-box">
            <div className="sak-countdown-num">{countdown.days}</div>
            <div className="sak-countdown-lbl">Days</div>
          </div>
          <div className="sak-countdown-box">
            <div className="sak-countdown-num">{countdown.hours}</div>
            <div className="sak-countdown-lbl">Hours</div>
          </div>
          <div className="sak-countdown-box">
            <div className="sak-countdown-num">{countdown.minutes}</div>
            <div className="sak-countdown-lbl">Mins</div>
          </div>
          <div className="sak-countdown-box">
            <div className="sak-countdown-num">{countdown.seconds}</div>
            <div className="sak-countdown-lbl">Secs</div>
          </div>
        </div>
      </header>

      {/* =========================================================================
          CHAPTER 2: FAMILIES & PHULKARI HERITAGE
          ========================================================================= */}
      <section className="sak-section">
        <div className="sak-phulkari-card text-center">
          <div className="w-10 h-10 mx-auto mb-4 text-[#d4af37]">
            <Heart size={32} className="mx-auto" />
          </div>

          <h2 className="sak-section-title">Families & Gratitude</h2>
          <p className="sak-section-subtitle">A Union of Two Heritage Lines</p>

          <p className="sak-serif text-lg md:text-xl text-[#fcf9f2] italic max-w-xl mx-auto leading-relaxed mb-8">
            “With humble hearts and the eternal grace of Guru Sahib, our families come together in joy,
            harmony, and thanksgiving to unite two souls on the spiritual path of marriage.”
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left border-t border-[#d4af37]/25 pt-8">
            <div className="bg-[#1c080b]/70 p-5 rounded-xl border border-[#d4af37]/20">
              <span className="text-[0.6875rem] font-bold uppercase tracking-wider text-[#d4af37] block mb-1">
                Groom’s Family
              </span>
              <h3 className="sak-serif text-2xl font-bold text-[#fff8eb]">
                {couple.groom}
              </h3>
              <p className="text-xs text-[#d6c6b2] mt-1">
                Son of S. Harbhajan Singh & Sdni. Manpreet Kaur
              </p>
            </div>

            <div className="bg-[#1c080b]/70 p-5 rounded-xl border border-[#d4af37]/20">
              <span className="text-[0.6875rem] font-bold uppercase tracking-wider text-[#d4af37] block mb-1">
                Bride’s Family
              </span>
              <h3 className="sak-serif text-2xl font-bold text-[#fff8eb]">
                {couple.bride}
              </h3>
              <p className="text-xs text-[#d6c6b2] mt-1">
                Daughter of S. Jaswinder Singh & Sdni. Ravinder Kaur
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          CHAPTER 3: TWILIGHT GURDWARA SAVE THE DATE
          ========================================================================= */}
      <section className="sak-section">
        <div className="sak-twilight-bg text-center">
          <GurdwaraDomeIllustration />

          <blockquote className="sak-gurbani-quote mt-6">
            “ਧਨ ਪਿਰੁ ਏਹਿ ਨ ਆਖੀਅਨਿ ਬਹਨਿ ਇਕਠੇ ਹੋਇ ॥<br />
            ਏਕ ਜੋਤਿ ਦੁਇ ਮੂਰਤੀ ਧਨ ਪਿਰੁ ਕਹੀਐ ਸੋਇ ॥”
          </blockquote>
          <p className="text-xs text-[#f3e5ab] max-w-md mx-auto mb-8 leading-relaxed">
            They are not said to be husband and wife who merely sit together. Rather, they alone are
            husband and wife who have one light in two bodies.
            <span className="block text-[0.6875rem] text-[#9382dc] mt-1 font-mono">
              — Sri Guru Granth Sahib Ji (Ang 788)
            </span>
          </p>

          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-[#180a0c]/80 border border-[#d4af37]/40 px-6 py-4 rounded-2xl backdrop-blur-md">
            <div className="text-center sm:text-left">
              <span className="text-[0.6875rem] uppercase tracking-widest text-[#d4af37] font-bold block">
                The Auspicious Date
              </span>
              <span className="sak-serif text-2xl font-bold text-[#fff8eb]">
                {parsedDate.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
            <a
              href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
                `${couple.groom} & ${couple.bride}'s Anand Karaj`,
              )}&dates=${parsedDate.toISOString().replace(/-|:|\.\d\d\d/g, '')}/${parsedDate
                .toISOString()
                .replace(/-|:|\.\d\d\d/g, '')}&location=${encodeURIComponent(
                data.eventVenue || 'Gurdwara Sahib',
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-[#180a0c] font-bold px-4 py-2 rounded-full text-xs uppercase tracking-wider hover:bg-[#ffd700] transition-colors"
            >
              <CalendarCheck size={14} /> Add to Calendar
            </a>
          </div>
        </div>
      </section>

      {/* =========================================================================
          CHAPTER 4: MULTI-EVENT ITINERARY & DRESS CODES
          ========================================================================= */}
      <section className="sak-section">
        <h2 className="sak-section-title">Wedding Programme</h2>
        <p className="sak-section-subtitle">Events, Timings & Dress Codes</p>

        <div className="sak-events-list">
          {ceremonies.map((ceremony, idx) => (
            <div key={ceremony.id || idx} className="sak-event-card">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#d4af37]/20 pb-3">
                <div>
                  <span className="text-[0.6875rem] font-bold uppercase tracking-wider text-[#d4af37]">
                    Chapter 0{idx + 1}
                  </span>
                  <h3 className="sak-serif text-2xl font-bold text-[#fff8eb]">
                    {ceremony.title}
                  </h3>
                </div>
                <div className="sak-dresscode-badge">
                  <span>Dress Code:</span> {ceremony.dressCode}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-[#f3e5ab]">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-[#d4af37] shrink-0" />
                  <span>{ceremony.date} · {ceremony.time}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#d4af37] shrink-0" />
                  <span>{ceremony.venue}</span>
                </div>
              </div>

              <p className="text-xs text-[#d6c6b2] leading-relaxed">
                {ceremony.note}
              </p>

              <div className="pt-2">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    ceremony.venue,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[#d4af37] hover:text-[#ffd700] font-semibold transition-colors"
                >
                  <Navigation size={13} /> View Route & Map
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* =========================================================================
          CHAPTER 5: GURDWARA ETIQUETTE (THINGS TO KNOW)
          ========================================================================= */}
      <section className="sak-section">
        <h2 className="sak-section-title">Gurdwara Etiquette</h2>
        <p className="sak-section-subtitle">Respectful Customs for All Honoured Guests</p>

        <div className="sak-etiquette-grid">
          <div className="sak-etiquette-box">
            <span className="sak-etiquette-icon">🧣</span>
            <h4 className="sak-serif text-xl font-bold text-[#f3e5ab] mb-2">Head Covering</h4>
            <p className="text-xs text-[#d6c6b2] leading-relaxed">
              All guests cover their heads with a Rumāl or scarf prior to entering. Scarves are warmly provided at the entrance.
            </p>
          </div>

          <div className="sak-etiquette-box">
            <span className="sak-etiquette-icon">👞</span>
            <h4 className="sak-serif text-xl font-bold text-[#f3e5ab] mb-2">Remove Footwear</h4>
            <p className="text-xs text-[#d6c6b2] leading-relaxed">
              Shoes and socks are deposited at the Joda Ghar (shoe station), followed by washing hands before entering the Darbar Sahib.
            </p>
          </div>

          <div className="sak-etiquette-box">
            <span className="sak-etiquette-icon">🙏</span>
            <h4 className="sak-serif text-xl font-bold text-[#f3e5ab] mb-2">Modest Seating</h4>
            <p className="text-xs text-[#d6c6b2] leading-relaxed">
              Guests sit comfortably cross-legged on the carpeted floor. Please ensure attire allows respectful floor seating.
            </p>
          </div>

          <div className="sak-etiquette-box">
            <span className="sak-etiquette-icon">🍲</span>
            <h4 className="sak-serif text-xl font-bold text-[#f3e5ab] mb-2">Guru Ka Langar</h4>
            <p className="text-xs text-[#d6c6b2] leading-relaxed">
              Following the Laavan, all guests are cordially invited to partake in blessed vegetarian Langar, served in the spirit of universal equality.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================================================
          CHAPTER 6: SACRED UNION BEAT & KHANDA
          ========================================================================= */}
      <section className="sak-section text-center">
        <div className="sak-khanda-emblem">
          <KhandaEmblem className="w-20 h-20 mx-auto" />
        </div>

        <h2 className="sak-section-title">The Four Laavan</h2>
        <p className="sak-section-subtitle">The Sacred Hymns of Spiritual Union</p>

        <p className="sak-serif text-xl md:text-2xl italic text-[#fcf9f2] max-w-2xl mx-auto leading-relaxed mb-4">
          “In the first round, the Lord instructs in duties of married life. In the second, the divine Beloved is met. In the third, the heart overflows with divine detachment. In the fourth, peaceful stillness and eternal union are attained.”
        </p>

        <p className="text-xs text-[#d6c6b2] max-w-lg mx-auto leading-relaxed">
          Composed by Guru Ram Das Ji, the Laavan circumambulate Sri Guru Granth Sahib Ji four times, sealing worldly fidelity in eternal oneness.
        </p>
      </section>

      {/* =========================================================================
          CHAPTER 7: SOCIAL HASHTAG & FILM
          ========================================================================= */}
      <section className="sak-section text-center">
        <div className="bg-gradient-to-r from-[#2c0e13] via-[#4a1820] to-[#2c0e13] border border-[#d4af37]/35 rounded-3xl p-8 shadow-2xl">
          <span className="text-[0.6875rem] font-bold uppercase tracking-widest text-[#d4af37] block mb-2">
            Capture & Share The Moments
          </span>
          <h2 className="sak-serif text-3xl md:text-4xl font-bold text-[#fff8eb] mb-4">
            {hashtag}
          </h2>
          <p className="text-xs text-[#d6c6b2] max-w-md mx-auto mb-6">
            Tag your photos and reels with our official celebration hashtag to be featured on our memories wall!
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                `Join us for ${couple.groom} & ${couple.bride}'s Anand Karaj wedding celebration! ${typeof window !== 'undefined' ? window.location.href : ''}`,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-5 py-2.5 rounded-full text-xs uppercase tracking-wider hover:brightness-110 transition-all shadow-lg"
            >
              <Share2 size={15} /> Share On WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* =========================================================================
          CHAPTER 8: RSVP & BLESSINGS WALL
          ========================================================================= */}
      <section id="rsvp" className="sak-section">
        <div className="bg-[#240c11]/90 border border-[#d4af37]/40 rounded-3xl p-6 sm:p-10 shadow-2xl">
          <h2 className="sak-section-title">RSVP & Blessings</h2>
          <p className="sak-section-subtitle">Please Kindly Confirm Your Attendance</p>

          {rsvp.state === 'success' ? (
            <div className="bg-[#1b4332]/80 border border-[#40916c] text-[#d8f3dc] p-6 rounded-2xl text-center">
              <CheckCircle2 size={40} className="mx-auto mb-2 text-[#52b788]" />
              <h3 className="sak-serif text-2xl font-bold mb-1">Dhanvaad! Your RSVP is Confirmed</h3>
              <p className="text-xs text-[#b7e4c7]">
                We look forward to rejoicing with you at the Anand Karaj celebration!
              </p>
            </div>
          ) : (
            <form onSubmit={handleRsvpSubmit} className="space-y-4 max-w-lg mx-auto">
              {rsvp.state === 'error' && (
                <div className="p-3 bg-red-900/60 border border-red-500 text-red-200 text-xs rounded-lg">
                  {rsvp.errorMessage || 'Unable to submit RSVP. Please try again.'}
                </div>
              )}

              <div>
                <label htmlFor="rsvp-name" className="block text-xs uppercase font-bold text-[#d4af37] tracking-wider mb-1">
                  Your Full Name *
                </label>
                <input
                  id="rsvp-name"
                  type="text"
                  required
                  placeholder="e.g. Navjot Singh"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="sak-input"
                />
              </div>

              <div>
                <label htmlFor="rsvp-email" className="block text-xs uppercase font-bold text-[#d4af37] tracking-wider mb-1">
                  Email Address
                </label>
                <input
                  id="rsvp-email"
                  type="email"
                  placeholder="e.g. navjot@example.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="sak-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <span className="block text-xs uppercase font-bold text-[#d4af37] tracking-wider mb-1">
                    Will You Attend?
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAttending('YES')}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all border ${
                        attending === 'YES'
                          ? 'bg-[#d4af37] text-[#180a0c] border-[#ffd700]'
                          : 'bg-[#180a0c] text-[#d6c6b2] border-[#d4af37]/30'
                      }`}
                    >
                      Yes, with Joy
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttending('NO')}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all border ${
                        attending === 'NO'
                          ? 'bg-rose-900 text-white border-rose-500'
                          : 'bg-[#180a0c] text-[#d6c6b2] border-[#d4af37]/30'
                      }`}
                    >
                      Regretfully
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="rsvp-guests" className="block text-xs uppercase font-bold text-[#d4af37] tracking-wider mb-1">
                    Total Guests
                  </label>
                  <select
                    id="rsvp-guests"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                    className="sak-input"
                  >
                    <option value="1">1 Person</option>
                    <option value="2">2 Persons</option>
                    <option value="3">3 Persons</option>
                    <option value="4">4 Persons</option>
                    <option value="5">5+ Family</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="rsvp-blessing" className="block text-xs uppercase font-bold text-[#d4af37] tracking-wider mb-1">
                  Blessing or Message for the Couple
                </label>
                <textarea
                  id="rsvp-blessing"
                  rows={3}
                  placeholder="Share your prayers, love, and warm wishes..."
                  value={blessingMessage}
                  onChange={(e) => setBlessingMessage(e.target.value)}
                  className="sak-input resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={rsvp.state === 'loading'}
                className="w-full py-3.5 bg-gradient-to-r from-[#d4af37] to-[#b8860b] text-[#180a0c] font-bold text-xs uppercase tracking-widest rounded-xl hover:brightness-110 active:scale-[0.99] transition-all shadow-xl flex items-center justify-center gap-2"
              >
                {rsvp.state === 'loading' ? (
                  <span>Submitting RSVP...</span>
                ) : (
                  <>
                    <Send size={15} /> Confirm RSVP & Send Blessings
                  </>
                )}
              </button>
            </form>
          )}

          {/* Blessing Wall */}
          <div className="mt-12 pt-8 border-t border-[#d4af37]/25">
            <div className="flex items-center gap-2 mb-4 justify-center">
              <Sparkles size={16} className="text-[#d4af37]" />
              <h3 className="sak-serif text-2xl font-bold text-[#fff8eb]">Blessings Wall</h3>
            </div>

            <div className="space-y-3 max-w-lg mx-auto">
              {wishes.map((w, idx) => (
                <div key={idx} className="bg-[#180a0c]/60 border border-[#d4af37]/20 p-4 rounded-xl text-left">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold text-xs text-[#f3e5ab]">{w.name}</span>
                    <span className="text-[0.625rem] text-[#d6c6b2]/60">{w.date}</span>
                  </div>
                  <p className="text-xs text-[#d6c6b2] italic leading-relaxed">
                    "{w.msg}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center py-12 border-t border-[#d4af37]/15 text-[#d6c6b2]/60 text-xs">
        <p className="sak-serif text-lg text-[#f3e5ab] mb-1">
          {couple.groom} & {couple.bride}
        </p>
        <p>Waheguru Ji Ka Khalsa, Waheguru Ji Ki Fateh</p>
      </footer>
    </div>
  );
}

export const SikhAnandKaraj = SikhAnandKarajTemplate;
registry.register({ metadata: META, component: SikhAnandKarajTemplate });
